// ---------------------------------------------------------------------------
// navOverride — types + serialize/hydrate helpers for the platform nav config
// v0.17.0 — storage moved to backend (see menuConfigApi.ts).
//
// Previously this module held localStorage CRUD for a per-browser override.
// As of v0.17.0 the override is platform-wide: a single backend row holds
// the canonical config, only platform_admin can write, all authenticated
// users read. This file now only holds:
//   - the serializable shape of NavItem (NavItemSerialized)
//   - the per-shell payload type (NavOverrideShell)
//   - serialize/hydrate helpers used by the editor and useShellNav
// ---------------------------------------------------------------------------

import type { NavItem, NavItemStatus } from "./adminNav";
import { getIcon, getIconName } from "./iconRegistry";

export type ShellKey = "admin" | "ops" | "client";

/** JSON-safe form of NavItem — icon stored as string name. */
export interface NavItemSerialized {
  name: string;
  href: string;
  iconName: string;
  matchPrefix?: string;
  requiredRole?: string[];
  requiredPermission?: string;
  children?: NavItemSerialized[];
  /**
   * Delivery status (#379). Payloads written before the field existed carry
   * nothing here; `normalizeAdminNavStatus` re-asserts the canonical value on
   * hydration, so storage is never the authority on what is legacy.
   */
  status?: NavItemStatus;
  /** Free-form annotation tags (v0.16.11). */
  tags?: string[];
}

export interface NavOverrideShell {
  main: NavItemSerialized[];
  bottom: NavItemSerialized[];
}

// ---------------------------------------------------------------------------
// Serialize / hydrate
// ---------------------------------------------------------------------------

export function serializeNavItem(item: NavItem): NavItemSerialized {
  return {
    name: item.name,
    href: item.href,
    iconName: getIconName(item.icon),
    matchPrefix: item.matchPrefix,
    requiredRole: item.requiredRole,
    requiredPermission: item.requiredPermission,
    children: item.children?.map(serializeNavItem),
    status: item.status,
    tags: item.tags,
  };
}

export function hydrateNavItem(item: NavItemSerialized): NavItem {
  return {
    name: item.name,
    href: item.href,
    icon: getIcon(item.iconName),
    matchPrefix: item.matchPrefix,
    requiredRole: item.requiredRole,
    requiredPermission: item.requiredPermission,
    children: item.children?.map(hydrateNavItem),
    status: item.status,
    tags: item.tags,
  };
}

export function serializeNavList(items: NavItem[]): NavItemSerialized[] {
  return items.map(serializeNavItem);
}

export function hydrateNavList(items: NavItemSerialized[]): NavItem[] {
  return items.map(hydrateNavItem);
}
